// mixpanel-browser is loaded lazily (dynamic import) so it stays OUT of the
// initial JS bundle on every page. The first analytics call triggers the load,
// which also runs init() exactly once — guaranteeing init happens before any
// track/identify call regardless of call order.
type Mixpanel = typeof import('mixpanel-browser').default;

let mixpanelPromise: Promise<Mixpanel> | null = null;

const loadMixpanel = (): Promise<Mixpanel> => {
  if (!mixpanelPromise) {
    mixpanelPromise = import('mixpanel-browser').then((mod) => {
      const mixpanel = mod.default;
      mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN!, {
        // autocapture: true,
        debug: process.env.NODE_ENV !== 'production',
        track_pageview: false,
        persistence: 'localStorage',
      });
      return mixpanel;
    });
  }
  return mixpanelPromise;
};

export const initMixpanel = (): void => {
  // Fire-and-forget: kicks off the lazy load + init.
  void loadMixpanel();
};

// Identify known user
export const identifyUser = async (
  userId: string,
  props?: Record<string, any>,
) => {
  const mixpanel = await loadMixpanel();
  mixpanel.identify(userId);
  if (props) mixpanel.people.set(props);
};

// Optional: alias on new signup
export const aliasUser = async (userId: string) => {
  const mixpanel = await loadMixpanel();
  mixpanel.alias(userId);
};

// Tracking
export const trackEvent = async (
  eventName: string,
  props?: Record<string, any>,
) => {
  const mixpanel = await loadMixpanel();
  mixpanel.track(eventName, {
    'Page Name': typeof window !== 'undefined' ? window.location.pathname : '',
    ...props,
  });
};

export const trackPageView = async (pageName?: string) => {
  await trackEvent('Page View', {
    'Page Name': pageName,
    'Event Type': 'page_view',
    'Operating System': window.navigator.platform,
    Browser: window.navigator.userAgent,
    'Browser Language': window.navigator.language,
    'Browser Timezone': new Date().getTimezoneOffset(),
    'Browser Cookies Enabled': navigator.cookieEnabled,
    'Track IP': true,
  });
};

export const trackBtnClick = async (buttonName: string, targetLink: string) => {
  await trackEvent('Click', {
    'Button Name': buttonName,
    'Target Link': targetLink,
    'Event Type': 'click',
  });
};

export const getAnonymousId = async (): Promise<string | null> => {
  const mixpanel = await loadMixpanel();
  return mixpanel.get_distinct_id() as string;
};
